import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  FormControl,
  Grid,
  Paper,
  Slider,
  useTheme,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ToggleSwitch from "./ToggleSwitch";
import RTLSwitch from "./RTLSwitch";
import { useColorMode } from "../context/ColorModeContext";
import { DrawerProps } from "../@types/types";

const drawerWidth = 350;

const colorOptions = ["#0336FF", "#F9AA33", "#FF0266"];

const fontFamilies = ["Roboto", "Narnoor", "Kanit", "Oxygen"];

const CutomizeDrawer: React.FC<DrawerProps> = ({ open, setDrawerOpen }) => {
  const theme = useTheme();
  const [selectedPrimaryColor, setSelectedPrimaryColor] = useState<string>(
    theme.palette.primary.main
  );
  const {
    setFontFamily,
    setPrimaryColor,
    setBorderRadius,
    handleResetSettings,
  } = useColorMode();

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const handlePrimaryColorChange = (color: string) => {
    setPrimaryColor(color);
    setSelectedPrimaryColor(color);
  };

  const handleBorderRadiusChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    setBorderRadius(newValue as number);
  };

  const handleFontFamilyChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFontFamily(event.target.value);
  };

  const onResetSettings = () => {
    handleResetSettings();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCloseDrawer}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        //display: { xs: "block", sm: "none" },
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          width: drawerWidth,
          bgcolor: "background.paper",
          boxShadow: "1px 0px 8px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <List>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Stack>
              <Typography variant="h5" gutterBottom>
                Settings
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Customize & Preview in Real Time
              </Typography>
            </Stack>
            <IconButton size="small" disableRipple onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
        <Divider />

        <ListItem>
          <FormControl fullWidth>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Primary Color
            </Typography>

            <Grid container sx={{ m: 1 }}>
              {colorOptions.map((color) => (
                <Grid key={color} item xs={4} sm={4} md={4} lg={4}>
                  <Box
                    sx={{
                      p: 2,
                      m: 1,
                      borderRadius: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: alpha(color, 0.1), // 50% opacity for lighter color
                      border: `1px dotted ${color}`,
                    }}
                  >
                    <Paper
                      sx={{
                        height: 30,
                        width: 30,
                        backgroundColor: color,
                        cursor: "pointer",
                        transform:
                          selectedPrimaryColor === color
                            ? "scale(1.2)"
                            : "scale(0.8)",
                        transition: "transform 0.3s ease",
                        border: `1px solid ${
                          selectedPrimaryColor === color
                            ? color
                            : "desiredBorderColorHere"
                        }`,
                      }}
                      onClick={() => handlePrimaryColorChange(color)}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </FormControl>
        </ListItem>

        <ListItem>
          <FormControl fullWidth>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Border Radius
            </Typography>
            <Box sx={{ width: "100%", m: 1 }}>
              <Slider
                size="small"
                value={theme.shape.borderRadius}
                onChange={handleBorderRadiusChange}
                valueLabelDisplay="auto"
                aria-labelledby="border-radius-slider"
                marks
                step={2}
                min={0}
                max={24}
                sx={{
                  width: "calc(100% - 48px)",
                }}
              />
            </Box>
          </FormControl>
        </ListItem>

        <ListItem>
          <FormControl fullWidth>
            <Typography variant="body1" color="text.secondary">
              Mode
            </Typography>
            <ToggleSwitch />
          </FormControl>
        </ListItem>

        <ListItem>
          <FormControl fullWidth>
            <Typography variant="body1" color="text.secondary">
              Direction
            </Typography>
            <RTLSwitch />
          </FormControl>
        </ListItem>

        <ListItem>
          <FormControl fullWidth>
            <Typography variant="body1" color="text.secondary">
              Font Family
            </Typography>
            <Box sx={{ width: "100%", m: 1 }}>
              <RadioGroup
                aria-label="font-family"
                value={theme.typography.fontFamily}
                onChange={handleFontFamilyChange}
                name="row-radio-buttons-group"
              >
                {fontFamilies.map((font) => (
                  <FormControlLabel
                    key={font}
                    value={font}
                    control={<Radio />}
                    label={font}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        color: (theme) => theme.palette.grey[900],
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </Box>
          </FormControl>
        </ListItem>

        <ListItem>
          <Box sx={{ width: "100%", textAlign: "center", m: 1 }}>
            <Button variant="outlined" onClick={onResetSettings}>
              Reset Settings
            </Button>
          </Box>
        </ListItem>
      </List>
      <Divider />
    </Drawer>
  );
};

export default CutomizeDrawer;
